<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("STOCK.INVENTORY.TITLE") }}</v-toolbar-title>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <StockAsOfDateBar
            v-if="userStatePrepared"
            :value="asOfDate"
            :hint="$t('STOCK.INVENTORY.AS_OF_DATE_HINT')"
            @input="onAsOfDateChange"
          />
          <v-card-text class="pa-0">
            <v-row v-if="summary" dense class="px-3 pt-3">
              <v-col cols="6" sm="2">
                <div class="caption grey--text">{{ $t("STOCK.INVENTORY.ON_HAND") }}</div>
                <div class="subtitle-2">{{ formatQty(summary.quantity_on_hand) }}</div>
              </v-col>
              <v-col cols="6" sm="2">
                <div class="caption grey--text">{{ $t("STOCK.INVENTORY.FREE") }}</div>
                <div class="subtitle-2" :class="{ 'error--text': Number(summary.free_quantity) < 0 }">
                  {{ formatQty(summary.free_quantity) }}
                </div>
              </v-col>
              <v-col cols="6" sm="2">
                <div class="caption grey--text">{{ $t("STOCK.INVENTORY.BLOCKED") }}</div>
                <div class="subtitle-2" :class="{ 'warning--text': Number(summary.blocked_quantity) > 0 }">
                  {{ formatQty(summary.blocked_quantity) }}
                </div>
              </v-col>
              <v-col cols="6" sm="2">
                <div class="caption grey--text">{{ $t("STOCK.INVENTORY.PROJECTED") }}</div>
                <div class="subtitle-2">{{ formatQty(summary.projected_quantity) }}</div>
              </v-col>
              <v-col cols="6" sm="2">
                <div class="caption grey--text">{{ $t("STOCK.INVENTORY.WEIGHTED_AVG_COST") }}</div>
                <div class="subtitle-2">
                  {{ formatMoney(summary.weighted_average_cost, summary.currency_code) }}
                </div>
              </v-col>
              <v-col cols="6" sm="2">
                <div class="caption grey--text">{{ $t("STOCK.INVENTORY.TOTAL_INVENTORY_VALUE") }}</div>
                <div class="subtitle-2">
                  {{ formatMoney(summary.total_inventory_value, summary.currency_code) }}
                </div>
              </v-col>
            </v-row>
            <v-alert
              v-if="summary && Number(summary.quantity_on_hand) > 0 && summary.weighted_average_cost == null"
              dense
              outlined
              type="info"
              class="mx-3 mb-0"
            >
              {{ $t("STOCK.INVENTORY.NO_AVG_COST_HINT") }}
            </v-alert>
            <div v-if="hasWarehouseScope" class="px-3 pt-2 stock-inventory-grouping">
              <div v-if="hasMultipleWarehouses" class="stock-inventory-grouping__item">
                <div class="caption grey--text">{{ $t("STOCK.WAREHOUSES.TITLE") }}</div>
                <v-btn-toggle v-model="groupBy" mandatory dense active-class="success white--text">
                  <v-btn value="warehouse" small>{{ $t("STOCK.INVENTORY.GROUP_BY_WAREHOUSE") }}</v-btn>
                  <v-btn value="product" small>{{ $t("STOCK.INVENTORY.GROUP_BY_PRODUCT") }}</v-btn>
                </v-btn-toggle>
              </div>
              <div
                v-for="option in detailGroupingOptions"
                :key="option.key"
                class="stock-inventory-grouping__item"
              >
                <div class="caption grey--text">{{ option.label }}</div>
                <v-btn-toggle
                  v-model="detailGrouping[option.key]"
                  mandatory
                  dense
                  active-class="success white--text"
                >
                  <v-btn value="detail" small>{{ option.perLabel }}</v-btn>
                  <v-btn value="grouped" small>{{ $t("STOCK.INVENTORY.GROUP_BY_PRODUCT") }}</v-btn>
                </v-btn-toggle>
              </div>
            </div>
            <v-alert
              v-if="!hasWarehouseScope"
              dense
              outlined
              type="info"
              class="ma-3 mb-0"
            >
              {{ $t("STOCK.INVENTORY.SCOPE_ORG_HINT") }}
            </v-alert>
            <v-alert
              v-if="hasWarehouseScope && showsTransitExpectedHint"
              dense
              outlined
              type="info"
              class="ma-3 mb-0"
            >
              {{ $t("STOCK.INVENTORY.TRANSIT_EXPECTED_HINT") }}
            </v-alert>
            <v-alert
              v-if="hasWarehouseScope && !hasSingleWarehouse"
              dense
              outlined
              type="info"
              class="ma-3 mb-0"
            >
              {{ $t("STOCK.INVENTORY.SINGLE_WAREHOUSE_FOR_LOTS") }}
            </v-alert>
            <Table
              v-if="userStatePrepared"
              ref="stock-inventory-table"
              height="500"
              name="stock-inventory-table"
              :headers="visibleHeaders"
              :server-data-options="{ method: tableItemsMethod }"
              :items-per-page="100"
              :loading="isProcessing"
              :filters="tableFilters"
              dense
              fixed-header
              multi-sort
              filterable
              @filters-change="onTableFiltersChange"
              @search="onTableSearchChange"
              @fetched-server-data="loadSummary"
            >
              <template
                v-for="(header, index) in visibleHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <template v-if="header.value === 'options'">
                  <v-btn
                    :key="`req-${index}`"
                    icon
                    small
                    color="primary"
                    class="mr-1"
                    :disabled="!hasWarehouseScope || !item.item.product"
                    :title="$t('STOCK.INVENTORY.REQUIREMENTS_LIST')"
                    @click="openRequirementsList(item.item)"
                  >
                    <v-icon small>mdi-calendar-clock</v-icon>
                  </v-btn>
                  <v-btn
                    :key="`lots-${index}`"
                    icon
                    small
                    color="primary"
                    class="mr-1"
                    :disabled="!hasSingleWarehouse || !item.item.product"
                    :title="$t('STOCK.INVENTORY.LOT_BREAKDOWN')"
                    @click="openLotBreakdown(item.item)"
                  >
                    <v-icon small>mdi-package-variant</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canCreateHold && !isBlockedAvailabilityRow(item.item)"
                    :key="`block-${index}`"
                    icon
                    small
                    color="warning"
                    class="mr-1"
                    :disabled="!canBlockRow(item.item)"
                    :title="$t('STOCK.INVENTORY.BLOCK_STOCK')"
                    @click="openBlockDialog(item.item)"
                  >
                    <v-icon small>mdi-lock</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canManageHolds && rowHasActiveHolds(item.item)"
                    :key="`holds-${index}`"
                    icon
                    small
                    color="warning"
                    class="mr-1"
                    :disabled="!canManageHoldsRow(item.item)"
                    :title="$t('STOCK.INVENTORY.UNBLOCK_STOCK')"
                    @click="openHoldsDialog(item.item)"
                  >
                    <v-icon small>mdi-lock-open</v-icon>
                  </v-btn>
                </template>
                <template v-else-if="header.value === 'product.code'">
                  <span :key="index">{{ item.item.product ? item.item.product.code : "-" }}</span>
                </template>
                <template v-else-if="header.value === 'product.display_name'">
                  <span :key="index">{{ item.item.product ? item.item.product.display_name : "-" }}</span>
                </template>
                <template v-else-if="header.value === 'product.stock_product_department.label'">
                  <span :key="index">
                    {{
                      item.item.product && item.item.product.stock_product_department
                        ? item.item.product.stock_product_department.label
                        : "-"
                    }}
                  </span>
                </template>
                <template v-else-if="header.value === 'product.stock_product_category.label'">
                  <span :key="index">
                    {{
                      item.item.product && item.item.product.stock_product_category
                        ? item.item.product.stock_product_category.label
                        : "-"
                    }}
                  </span>
                </template>
                <template v-else-if="header.value === 'product.temperature.label'">
                  <span :key="index">
                    {{
                      item.item.product && item.item.product.temperature
                        ? item.item.product.temperature.label
                        : "-"
                    }}
                  </span>
                </template>
                <template v-else-if="header.value === 'quantity_breakdown'">
                  <StockPackagingBreakdownCell
                    :key="index"
                    :breakdown="item.item.quantity_breakdown"
                  />
                </template>
                <template v-else-if="header.value === 'product.origin_country.label'">
                  <span :key="index">
                    {{
                      item.item.product && item.item.product.origin_country
                        ? item.item.product.origin_country.label
                        : "-"
                    }}
                  </span>
                </template>
                <template v-else-if="header.value === 'product.stock_tags'">
                  <template
                    v-if="item.item.product && item.item.product.stock_tags && item.item.product.stock_tags.length"
                  >
                    <v-chip
                      v-for="tag in item.item.product.stock_tags"
                      :key="tag.id"
                      x-small
                      class="ma-1"
                      color="teal"
                      dark
                    >
                      {{ tag.label }}
                    </v-chip>
                  </template>
                  <span v-else :key="index">-</span>
                </template>
                <template v-else-if="header.value === 'product.primary_unit.code'">
                  <span :key="index">
                    {{
                      item.item.product && item.item.product.primary_unit
                        ? item.item.product.primary_unit.code
                        : "-"
                    }}
                  </span>
                </template>
                <template v-else-if="header.value === 'warehouse.name'">
                  {{ item.item.warehouse ? item.item.warehouse.name : $t("GENERAL.LABELS.ALL") }}
                </template>
                <template v-else-if="header.value === 'lot_number'">
                  {{ item.item.lot_number || $t("STOCK.INVENTORY.NO_LOT") }}
                </template>
                <template v-else-if="header.value === 'availability_status'">
                  <v-chip
                    v-if="item.item.availability_status === 'blocked'"
                    x-small
                    color="warning"
                    dark
                  >
                    {{ $t("STOCK.INVENTORY.STATUS_BLOCKED") }}
                  </v-chip>
                  <v-chip
                    v-else-if="item.item.availability_status === 'free'"
                    x-small
                    color="success"
                    dark
                  >
                    {{ $t("STOCK.INVENTORY.STATUS_FREE") }}
                  </v-chip>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'days_until_expiry'">
                  <v-tooltip v-if="item.item.expiry_date" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        v-bind="attrs"
                        v-on="on"
                        :class="{
                          'error--text font-weight-medium':
                            daysUntilExpiry(item.item) < 0,
                        }"
                      >
                        {{ daysUntilExpiry(item.item) }}
                      </span>
                    </template>
                    <span>
                      {{ $t("STOCK.INVENTORY.EXPIRY_DATE") }}:
                      {{ item.item.expiry_date }}
                    </span>
                  </v-tooltip>
                  <span v-else>—</span>
                </template>
                <template v-else-if="header.value === 'external_lot_number'">
                  {{ item.item.external_lot_number || "-" }}
                </template>
                <template v-else-if="header.value === 'deal.label'">
                  {{ dealLabel(item.item.deal) }}
                </template>
                <template v-else-if="header.value === 'company.label'">
                  {{ companyLabel(item.item.company) }}
                </template>
                <template v-else-if="header.value === 'free_quantity'">
                  <span
                    :key="index"
                    :class="{ 'error--text font-weight-medium': Number(item.item.free_quantity) < 0 }"
                  >
                    {{ item.value }}
                  </span>
                </template>
                <template v-else>
                  {{ item.value }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <StockInventoryLotBreakdownDialog
      v-model="showLotDialog"
      :stock-product-id="lotDialogProductId"
      :as-of-date="currentAsOfDate"
      :product-display-name="lotDialogProductName"
    />
    <StockInventoryRequirementsListDialog
      v-model="showRequirementsDialog"
      :stock-product-id="requirementsDialogProductId"
      :warehouse-id="requirementsDialogWarehouseId"
      :as-of-date="currentAsOfDate"
      :product-display-name="requirementsDialogProductName"
    />
    <StockQualityHoldDialog
      v-model="showBlockDialog"
      :warehouse-id="holdDialogWarehouseId"
      :warehouse-label="holdDialogWarehouseLabel"
      :stock-product-id="holdDialogProductId"
      :product-label="holdDialogProductLabel"
      :stock-lot-id="holdDialogLotId"
      :as-of-date="currentAsOfDate"
      @saved="reloadTable"
    />
    <StockQualityHoldsListDialog
      v-model="showHoldsDialog"
      :warehouse-id="holdDialogWarehouseId"
      :warehouse-label="holdDialogWarehouseLabel"
      :stock-product-id="holdDialogProductId"
      :product-label="holdDialogProductLabel"
      :stock-lot-id="holdDialogLotId"
      :lot-number="holdDialogLotNumber"
      @saved="reloadTable"
    />
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import StockAsOfDateBar from "@/views/partials/stock/StockAsOfDateBar.vue";
import StockPackagingBreakdownCell from "@/views/partials/stock/StockPackagingBreakdownCell.vue";
import StockInventoryLotBreakdownDialog from "@/views/partials/stock/StockInventoryLotBreakdownDialog.vue";
import StockInventoryRequirementsListDialog from "@/views/partials/stock/StockInventoryRequirementsListDialog.vue";
import StockQualityHoldDialog from "@/views/partials/stock/StockQualityHoldDialog.vue";
import StockQualityHoldsListDialog from "@/views/partials/stock/StockQualityHoldsListDialog.vue";
import stockAsOfDateTableMixin from "@/views/partials/stock/stockAsOfDateTable.mixin";
import { GET_STOCK_INVENTORY, GET_STOCK_INVENTORY_SUMMARY } from "@/store/stock/stockInventory.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS } from "@/store/stock/stockProductDepartments.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";
import {
  GET_STOCK_TEMPERATURE_OPTIONS,
  GET_STOCK_TAG_OPTIONS,
} from "@/store/stock/stockProducts.module";

export default {
  name: "StockInventory",
  mixins: [stockAsOfDateTableMixin],
  components: {
    Table,
    StockAsOfDateBar,
    StockPackagingBreakdownCell,
    StockInventoryLotBreakdownDialog,
    StockInventoryRequirementsListDialog,
    StockQualityHoldDialog,
    StockQualityHoldsListDialog,
  },
  data() {
    return {
      stockTableRef: "stock-inventory-table",
      tab: "tab-1",
      isProcessing: false,
      userStatePrepared: false,
      tableItemsMethod: GET_STOCK_INVENTORY,
      tableFilters: [],
      allDepartments: [],
      allCategories: [],
      activeDepartmentIds: [],
      showLotDialog: false,
      lotDialogProductId: null,
      lotDialogProductName: "",
      showRequirementsDialog: false,
      requirementsDialogProductId: null,
      requirementsDialogWarehouseId: null,
      requirementsDialogProductName: "",
      showBlockDialog: false,
      showHoldsDialog: false,
      holdDialogWarehouseId: null,
      holdDialogWarehouseLabel: "",
      holdDialogProductId: null,
      holdDialogProductLabel: "",
      holdDialogLotId: null,
      holdDialogLotNumber: "",
      groupBy: "warehouse",
      detailGrouping: {
        availability: "detail",
        lot: "grouped",
        external_lot: "grouped",
        deal: "grouped",
        company: "grouped",
      },
      summary: null,
      scopedWarehouses: [],
      headers: [
        { text: this.$t("STOCK.PRODUCTS.CODE"), value: "product.code", align: "center", width: "7%" },
        {
          text: this.$t("STOCK.PRODUCTS.DISPLAY_NAME"),
          value: "product.display_name",
          align: "center",
          width: "14%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.DEPARTMENT"),
          value: "product.stock_product_department.label",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.CATEGORY"),
          value: "product.stock_product_category.label",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.TEMPERATURE"),
          value: "product.temperature.label",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.ORIGIN_COUNTRY"),
          value: "product.origin_country.label",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.TAGS"),
          value: "product.stock_tags",
          sortable: false,
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.UNIT"),
          value: "product.primary_unit.code",
          sortable: false,
          align: "center",
          width: "6%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.TITLE"),
          value: "warehouse.name",
          sortable: false,
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("STOCK.INVENTORY.LOT"),
          value: "lot_number",
          sortable: false,
          align: "center",
          width: "8%",
          optionalGrouping: "lot",
        },
        {
          text: this.$t("STOCK.INVENTORY.DAYS_TO_EXPIRY"),
          value: "days_until_expiry",
          sortable: false,
          align: "center",
          width: "7%",
          optionalGrouping: "lot",
        },
        {
          text: this.$t("STOCK.INVENTORY.EXTERNAL_LOT"),
          value: "external_lot_number",
          sortable: false,
          align: "center",
          width: "8%",
          optionalGrouping: "external_lot",
        },
        {
          text: this.$t("STOCK.INVENTORY.DEAL"),
          value: "deal.label",
          sortable: false,
          align: "center",
          width: "9%",
          optionalGrouping: "deal",
        },
        {
          text: this.$t("STOCK.INVENTORY.COMPANY"),
          value: "company.label",
          sortable: false,
          align: "center",
          width: "10%",
          optionalGrouping: "company",
        },
        {
          text: this.$t("STOCK.INVENTORY.AVAILABILITY"),
          value: "availability_status",
          sortable: false,
          align: "center",
          width: "7%",
          optionalGrouping: "availability",
        },
        {
          text: this.$t("STOCK.PRODUCTS.PACKAGING"),
          value: "quantity_breakdown",
          sortable: false,
          align: "left",
          width: "16%",
        },
        { text: this.$t("STOCK.INVENTORY.ON_HAND"), value: "quantity_on_hand", align: "center", width: "6%" },
        {
          text: this.$t("STOCK.INVENTORY.RESERVED"),
          value: "reserved_quantity",
          align: "center",
          width: "6%",
          hideOnDetailGrouping: true,
        },
        {
          text: this.$t("STOCK.INVENTORY.BLOCKED"),
          value: "blocked_quantity",
          align: "center",
          width: "5%",
          hideOnDetailGrouping: true,
        },
        {
          text: this.$t("STOCK.INVENTORY.FREE"),
          value: "free_quantity",
          align: "center",
          width: "5%",
          hideOnDetailGrouping: true,
        },
        {
          text: this.$t("STOCK.INVENTORY.EXPECTED"),
          value: "expected_quantity",
          align: "center",
          width: "5%",
          hideOnDetailGrouping: true,
        },
        {
          text: this.$t("STOCK.INVENTORY.PROJECTED"),
          value: "projected_quantity",
          align: "center",
          width: "5%",
          hideOnDetailGrouping: true,
        },
        {
          text: this.$t("STOCK.INVENTORY.LATEST_PLANNED_DATE"),
          value: "latest_planned_date",
          sortable: false,
          align: "center",
          width: "7%",
          hideOnDetailGrouping: true,
        },
        {
          text: "",
          value: "options",
          sortable: false,
          align: "center",
          width: "12%",
        },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
    hasWarehouseScope() {
      return (this.$store.getters.stockContextWarehouseIds || []).length > 0;
    },
    hasSingleWarehouse() {
      return (this.$store.getters.stockContextWarehouseIds || []).length === 1;
    },
    currentAsOfDate() {
      return this.asOfDate;
    },
    hasMultipleWarehouses() {
      return (this.$store.getters.stockContextWarehouseIds || []).length > 1;
    },
    detailGroupingOptions() {
      return [
        {
          key: "availability",
          label: this.$t("STOCK.INVENTORY.AVAILABILITY"),
          perLabel: this.$t("STOCK.INVENTORY.GROUP_BY_AVAILABILITY"),
        },
        {
          key: "lot",
          label: this.$t("STOCK.INVENTORY.LOT"),
          perLabel: this.$t("STOCK.INVENTORY.GROUP_BY_LOT"),
        },
        {
          key: "external_lot",
          label: this.$t("STOCK.INVENTORY.EXTERNAL_LOT"),
          perLabel: this.$t("STOCK.INVENTORY.GROUP_BY_EXTERNAL_LOT"),
        },
        {
          key: "deal",
          label: this.$t("STOCK.INVENTORY.DEAL"),
          perLabel: this.$t("STOCK.INVENTORY.GROUP_BY_DEAL"),
        },
        {
          key: "company",
          label: this.$t("STOCK.INVENTORY.COMPANY"),
          perLabel: this.$t("STOCK.INVENTORY.GROUP_BY_COMPANY"),
        },
      ];
    },
    canCreateHold() {
      return this.hasPermissions("stock-quality-holds-create");
    },
    canManageHolds() {
      return this.canCreateHold
        || this.hasPermissions("stock-quality-holds-view-all")
        || this.hasPermissions("stock-inventory-view-all")
        || this.hasPermissions("stock-quality-holds-release");
    },
    visibleHeaders() {
      return this.headers.filter((header) => {
        if (header.hideOnDetailGrouping && this.hasDetailGrouping) {
          return false;
        }

        if (header.optionalGrouping === "availability") {
          return this.isDetailGrouped("availability");
        }

        if (!header.optionalGrouping) {
          return true;
        }

        return this.isDetailGrouped(header.optionalGrouping);
      });
    },
    hasDetailGrouping() {
      return this.detailGroupingOptions
        .filter((option) => option.key !== "availability")
        .some((option) => this.isDetailGrouped(option.key));
    },
    showsTransitExpectedHint() {
      return (this.scopedWarehouses || []).some(
        (warehouse) =>
          warehouse.type === "virtual"
          && warehouse.virtual_subtype === "transit"
      );
    },
  },
  watch: {
    stockContextVersion() {
      this.reloadTable();
      this.loadScopedWarehouseMeta();
      this.loadSummary();
    },
    groupBy() {
      this.applyGroupByToTable();
      this.loadSummary();
    },
    detailGrouping: {
      deep: true,
      handler() {
        this.applyGroupByToTable();
        this.loadSummary();
      },
    },
    userStatePrepared(ready) {
      if (ready) {
        this.$nextTick(() => {
          this.initAsOfDateFromTable(this.stockTableRef, { useToday: true });
          this.applyGroupByToTable();
          this.loadSummary();
        });
      }
    },
  },
  mounted() {
    this.loadFilters().finally(() => {
      this.userStatePrepared = true;
      this.loadScopedWarehouseMeta();
    });
  },
  methods: {
    async loadScopedWarehouseMeta() {
      const warehouseIds = (this.$store.getters.stockContextWarehouseIds || []).map(Number);
      if (warehouseIds.length === 0) {
        this.scopedWarehouses = [];
        return;
      }

      try {
        const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, {
          warehouse_ids: warehouseIds,
        });
        this.scopedWarehouses = data.data || data;
      } catch (error) {
        this.scopedWarehouses = [];
      }
    },
    loadFilters() {
      return Promise.all([
        this.$store.dispatch(GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_TEMPERATURE_OPTIONS),
        this.$store.dispatch(GET_STOCK_TAG_OPTIONS, {}),
      ]).then(([departmentsResponse, categoriesResponse, temperaturesResponse, tagsResponse]) => {
        this.allDepartments = departmentsResponse.data || [];
        this.allCategories = categoriesResponse.data || [];
        this.buildTableFilters(
          this.allDepartments,
          this.getFilteredCategories([]),
          temperaturesResponse.data || [],
          tagsResponse.data || []
        );
      });
    },
    getFilteredCategories(departmentIds) {
      if (!departmentIds.length) {
        return this.allCategories;
      }

      return this.allCategories.filter((category) =>
        departmentIds.includes(category.stock_product_department_id)
      );
    },
    buildTableFilters(departments, categories, temperatures, tags) {
      this.tableFilters = [
        {
          key: "stock_product_department_id",
          label: this.$t("STOCK.PRODUCTS.DEPARTMENT"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: departments,
        },
        {
          key: "stock_product_category_id",
          label: this.$t("STOCK.PRODUCTS.CATEGORY"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: categories,
        },
        {
          key: "temperature_id",
          label: this.$t("STOCK.PRODUCTS.TEMPERATURE"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: temperatures,
        },
        {
          key: "stock_tag_ids",
          label: this.$t("STOCK.PRODUCTS.TAGS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: tags,
        },
      ];
    },
    pruneCategoryFilterSelection(filters, categories) {
      const selectedCategoryIds = filters.stock_product_category_id || [];
      if (!selectedCategoryIds.length) {
        return false;
      }

      const allowedIds = new Set(categories.map((category) => category.id));
      const pruned = selectedCategoryIds.filter((id) => allowedIds.has(id));
      if (JSON.stringify(pruned) === JSON.stringify(selectedCategoryIds)) {
        return false;
      }

      filters.stock_product_category_id = pruned;
      const table = this.$refs[this.stockTableRef];
      if (table) {
        table.serverFilters = {
          ...table.serverFilters,
          ...filters,
        };
      }
      return true;
    },
    onTableFiltersChange(filters = {}) {
      const departmentIds = filters.stock_product_department_id || [];
      const departmentChanged =
        JSON.stringify(departmentIds) !== JSON.stringify(this.activeDepartmentIds);

      if (departmentChanged) {
        this.activeDepartmentIds = [...departmentIds];
        const filteredCategories = this.getFilteredCategories(departmentIds);
        const pruned = this.pruneCategoryFilterSelection(filters, filteredCategories);
        this.buildTableFilters(
          this.allDepartments,
          filteredCategories,
          this.tableFilters.find((filter) => filter.key === "temperature_id")?.items || [],
          this.tableFilters.find((filter) => filter.key === "stock_tag_ids")?.items || []
        );
        if (pruned) {
          this.reloadTable();
          return;
        }
      }

      this.loadSummary();
    },
    reloadTable() {
      this.$nextTick(() => {
        this.applyGroupByToTable();
        this.$refs["stock-inventory-table"]?.fetchServerData();
        this.loadSummary();
      });
    },
    applyGroupByToTable() {
      const table = this.$refs[this.stockTableRef];
      if (!table) {
        return;
      }

      const merged = {
        ...(table.serverFilters || {}),
        group_by: this.groupBy,
        group_by_warehouse: this.groupBy === "warehouse",
        group_by_lot: this.isDetailGrouped("lot"),
        group_by_external_lot: this.isDetailGrouped("external_lot"),
        group_by_deal: this.isDetailGrouped("deal"),
        group_by_company: this.isDetailGrouped("company"),
        group_by_availability: this.isDetailGrouped("availability"),
        as_of_date: this.asOfDate || this.todayIsoDate(),
      };

      table.serverFilters = merged;
      table.serverHandleFiltersChange(merged, table.options);
    },
    onAsOfDateChange(value) {
      this.asOfDate = value || this.todayIsoDate();
      this.$nextTick(() => {
        this.applyAsOfDateToTable(this.stockTableRef);
        this.applyGroupByToTable();
        this.loadSummary();
      });
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    loadSummary() {
      if (!this.hasWarehouseScope) {
        this.summary = null;
        return;
      }
      this.$store
        .dispatch(GET_STOCK_INVENTORY_SUMMARY, this.buildSummaryParams())
        .then((data) => {
          this.summary = data;
        })
        .catch(() => {
          this.summary = null;
        });
    },
    buildSummaryParams() {
      const table = this.$refs[this.stockTableRef];
      const serverFilters = table?.serverFilters || {};
      const search = (table?.searchString || "").trim();

      return {
        as_of_date: this.asOfDate,
        group_by: this.groupBy,
        group_by_warehouse: this.groupBy === "warehouse",
        group_by_lot: this.isDetailGrouped("lot"),
        group_by_external_lot: this.isDetailGrouped("external_lot"),
        group_by_deal: this.isDetailGrouped("deal"),
        group_by_company: this.isDetailGrouped("company"),
        group_by_availability: this.isDetailGrouped("availability"),
        search: search.length >= 2 ? search : undefined,
        filters: serverFilters,
      };
    },
    canBlockRow(row) {
      return this.hasSingleWarehouse
        && row
        && row.product
        && !this.isBlockedAvailabilityRow(row)
        && Number(row.quantity_on_hand || 0) > 0;
    },
    isBlockedAvailabilityRow(row) {
      return row && row.availability_status === "blocked";
    },
    hasBlockedQty(row) {
      return row && Number(row.blocked_quantity || 0) > 0.0001;
    },
    rowHasActiveHolds(row) {
      return this.isBlockedAvailabilityRow(row) || this.hasBlockedQty(row);
    },
    canManageHoldsRow(row) {
      return this.hasSingleWarehouse && row && row.product;
    },
    openBlockDialog(row) {
      if (!this.canBlockRow(row)) {
        return;
      }
      this.fillHoldDialogContext(row);
      this.showBlockDialog = true;
    },
    openHoldsDialog(row) {
      if (!this.canManageHoldsRow(row)) {
        return;
      }
      this.fillHoldDialogContext(row);
      this.showHoldsDialog = true;
    },
    fillHoldDialogContext(row) {
      const warehouseIds = this.$store.getters.stockContextWarehouseIds || [];
      this.holdDialogWarehouseId = row.warehouse?.id
        ? Number(row.warehouse.id)
        : Number(warehouseIds[0]);
      this.holdDialogWarehouseLabel = row.warehouse?.name
        || row.warehouse?.label
        || "";
      this.holdDialogProductId = Number(row.product.id);
      this.holdDialogProductLabel = row.product.display_name || row.product.code || "";
      this.holdDialogLotId = row.stock_lot_id ? Number(row.stock_lot_id) : null;
      this.holdDialogLotNumber = row.lot_number || "";
    },
    onTableSearchChange() {
      this.loadSummary();
    },
    formatMoney(value, currency) {
      if (value == null || value === "") {
        return "—";
      }
      return `${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })} ${currency || ""}`.trim();
    },
    isDetailGrouped(key) {
      return this.detailGrouping[key] === "detail";
    },
    daysUntilExpiry(row) {
      if (!row || !row.expiry_date) {
        return null;
      }

      const asOf = row.as_of_date || this.asOfDate || this.todayIsoDate();
      const expiry = new Date(`${row.expiry_date}T00:00:00`);
      const asOfDate = new Date(`${asOf}T00:00:00`);

      if (Number.isNaN(expiry.getTime()) || Number.isNaN(asOfDate.getTime())) {
        return null;
      }

      return Math.round((expiry.getTime() - asOfDate.getTime()) / 86400000);
    },
    openLotBreakdown(row) {
      if (!row.product || !this.hasSingleWarehouse) {
        return;
      }
      this.lotDialogProductId = row.product.id;
      this.lotDialogProductName = row.product.display_name || row.product.code || "";
      this.showLotDialog = true;
    },
    openRequirementsList(row) {
      if (!row.product || !this.hasWarehouseScope) {
        return;
      }
      this.requirementsDialogProductId = row.product.id;
      this.requirementsDialogWarehouseId =
        this.groupBy === "warehouse" && row.warehouse && row.warehouse.id
          ? row.warehouse.id
          : null;
      this.requirementsDialogProductName = row.product.display_name || row.product.code || "";
      this.showRequirementsDialog = true;
    },
    dealLabel(deal) {
      if (!deal) {
        return "-";
      }

      if (deal.label) {
        return deal.label;
      }

      if (deal.code && deal.name) {
        return `${deal.code} - ${deal.name}`;
      }

      return deal.name || deal.code || "-";
    },
    companyLabel(company) {
      if (!company) {
        return "-";
      }

      return company.label || company.name || company.company_name || "-";
    },
  },
};
</script>

<style scoped>
.stock-inventory-grouping {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.stock-inventory-grouping__item {
  min-width: 150px;
}
</style>
